/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, LegacyRef, useRef } from 'react';
import PlacesAutocomplete, { geocodeByAddress, PropTypes, Suggestion } from 'react-places-autocomplete';
import { isEmpty } from '@belong/common';
import { Text, TextInput } from '@belong/ui';
import clsx from 'clsx';
import { useField } from 'formik';
import { INVALID_ZIPCODE } from 'src/constants/address';
import { GoogleMapsApi } from 'src/features/document/google-maps-api';
import { useStore } from 'src/store';
import { setGoogleMapsScriptLoadedSelector, isGoogleMapsLoadedSelector } from 'src/store/scripts';
import { parseGeocodeAddress } from './utils';

const googleMapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
const defaultSearchOptions = {
  componentRestrictions: { country: 'us' },
  types: ['address'],
};

type PlacesAutocompleteExtended = PlacesAutocomplete & {
  handleInputOnBlur: () => void;
};

export const AddressAutocomplete = forwardRef<Ref, Props>(function AddressAutocomplete(
  {
    id,
    name,
    displayStreetAddressOnly,
    validate,
    focused,
    label,
    disabled,
    searchOptions,
    onAdressSelect,
    onValueChange,
    variant,
    autoComplete = 'off',
    className,
    textInputProps = {},
  },
  ref
) {
  const setGoogleMapsScriptLoaded = useStore(setGoogleMapsScriptLoadedSelector);
  const isGoogleMapsLoaded = useStore(isGoogleMapsLoadedSelector);
  const [{ value: fieldValue, onChange, ...field }, meta, { setValue, setTouched }] = useField({
    name,
    validate,
  });

  const placesAutocompleteRef = useRef<PlacesAutocompleteExtended>();

  function handleChange(value: string) {
    if (onValueChange) {
      onValueChange(value);
    }

    if (value === '') {
      return setValue({ address: '' });
    }

    return setValue({
      ...fieldValue,
      address: value,
      zipcode: isEmpty(fieldValue.zipcode) ? INVALID_ZIPCODE : fieldValue.zipcode,
    });
  }

  function handleSelect(value: string) {
    return geocodeByAddress(value).then((results) => {
      const parsedAddress = parseGeocodeAddress(results[0]);
      const newValue = {
        address: value,
        ...parsedAddress,
        zipcode: isEmpty(parsedAddress.zipcode) ? INVALID_ZIPCODE : parsedAddress.zipcode,
      };

      // This is used for the design where we want the field to only show the street address
      if (displayStreetAddressOnly) {
        newValue.address = newValue.streetAddress;
      }

      if (onAdressSelect) {
        onAdressSelect(newValue);
      }

      return setValue(newValue);
    });
  }

  function handleBlur(suggestions: readonly Suggestion[]) {
    setTouched(true);
    if (!suggestions.length) return;

    handleSelect(suggestions[0].description);
    placesAutocompleteRef.current?.handleInputOnBlur();
  }

  let errorMessage: string;
  if (meta.touched && meta.error) {
    errorMessage = meta.error;
  }

  const addressValue = fieldValue.address || '';

  return (
    <>
      {googleMapsApiKey && <GoogleMapsApi googleMapsApiKey={googleMapsApiKey} onLoad={setGoogleMapsScriptLoaded} />}

      {/* TODO: ADD SKELLETON FOR THIS WHILE LIBRARY IS LOADING */}
      {isGoogleMapsLoaded && (
        <PlacesAutocomplete
          highlightFirstSuggestion
          value={addressValue}
          onChange={handleChange}
          onSelect={handleSelect}
          searchOptions={{ ...defaultSearchOptions, ...searchOptions }}
          ref={placesAutocompleteRef as LegacyRef<PlacesAutocompleteExtended>}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps }) => {
            const inputProps = getInputProps<TextInputProps>({
              label,
            });

            return (
              <div className="relative">
                <TextInput
                  id={id}
                  ref={ref}
                  {...inputProps}
                  {...field}
                  focused={focused}
                  error={errorMessage}
                  disabled={disabled}
                  variant={variant}
                  onBlur={() => handleBlur(suggestions)}
                  autoComplete={autoComplete}
                  className={className}
                  {...textInputProps}
                />

                {!!suggestions.length && (
                  <div className="rounded border border-solid border-gray overflow-hidden absolute bg-white z-50 w-full">
                    {suggestions.map((suggestion) => {
                      const suggestionItem = getSuggestionItemProps(suggestion, {
                        className: clsx('w-full p-xs md:pr-3xl md:pl-md text-left', {
                          'cursor-pointer bg-light-gray': suggestion.active,
                        }),
                      });

                      return (
                        <div {...suggestionItem} key={suggestion.placeId}>
                          <SuggestionItem description={suggestion.description} value={fieldValue.address} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }}
        </PlacesAutocomplete>
      )}
    </>
  );
});

// Will not support outsideUS for initial launch
function SuggestionItem({ description, value }: SuggestionItemProps) {
  const slicedDescription = description.slice(0, description.length - 5);
  const offset = slicedDescription.toLowerCase().indexOf(value.toLowerCase());
  const { length } = value;

  const pre = slicedDescription.slice(0, offset);
  const highlight = slicedDescription.slice(offset, offset + length);
  const post = slicedDescription.slice(offset + length);

  if (offset === -1) {
    return <>{slicedDescription}</>;
  }

  return (
    <span translate="no">
      {pre && <Text as="span">{pre}</Text>}
      {highlight && (
        <Text as="span" fontWeight="semibold">
          {highlight}
        </Text>
      )}
      {post && <Text as="span">{post}</Text>}
    </span>
  );
}

type TextInputProps = React.ComponentProps<typeof TextInput>;

type PlacesAutocompleteProps = Pick<PropTypes, 'searchOptions'>;

type Ref = HTMLInputElement;

type Props = TextInputProps &
  PlacesAutocompleteProps & {
    validate?: (value: any) => undefined | string | Promise<any>;
    name: string;
    onAdressSelect?: (address: any) => void;
    onValueChange?: (address: any) => void;
    autoFocus?: boolean;
    variant?: 'primary' | 'secondary' | 'filled';
    displayStreetAddressOnly?: boolean;
    textInputProps?: Partial<TextInputProps>;
  };

type SuggestionItemProps = {
  description: string;
  value: string;
};
